import React from 'react';
import { FetchResult } from '@apollo/client';

import Comment from 'buyside/components/presentation/Feed/Comments/Comment/comment';

import {
  AnonUser,
  Comment as CommentType,
  LikeCommentMutation,
  Maybe,
  User,
  useLikeCommentMutation
} from 'interfaces/graphql-typings';

type CommentAnonUser = Pick<AnonUser, 'aumRange' | 'fundType' | 'title'>;
type CommentUser = Pick<User, 'id' | 'displayName' | 'profileImageUrl'>;

type CurrentCommentType = Pick<
  CommentType,
  | 'id'
  | 'anonymous'
  | 'body'
  | 'byAllocator'
  | 'createdAt'
  | 'commentableType'
  | 'commentCreationTime'
  | 'currentUserHasLiked'
  | 'likeCount'
  | 'parentId'
> & {
  anonUser?: Maybe<CommentAnonUser>;
} & {
  user?: Maybe<CommentUser>;
};

const CommentContainer: React.FC<{
  allComments: Maybe<CurrentCommentType>[];
  canCommentAnonymously: boolean;
  comment: CurrentCommentType;
  feedItemId?: string;
  isAllocator: boolean;
  mentionables: any;
  postId: string;
  profileImageUrl?: Maybe<string>;
  showLikes: boolean;
}> = ({
  allComments,
  canCommentAnonymously,
  comment,
  feedItemId,
  isAllocator,
  mentionables,
  postId,
  profileImageUrl,
  showLikes
}): React.ReactElement => {
  const [likeComment] = useLikeCommentMutation({
    update(cache, { data }) {
      if (!data?.likeComment?.comment) return;

      const hasLiked = data.likeComment.comment.currentUserHasLiked;

      cache.modify({
        id: cache.identify({ id: data.likeComment.comment.id, __typename: 'Comment' }),
        fields: { currentUserHasLiked: () => hasLiked }
      });
    }
  });

  const childComments = allComments.filter(c => c && c.parentId === comment.id);

  return (
    <Comment
      allComments={allComments}
      canCommentAnonymously={canCommentAnonymously}
      childComments={childComments}
      comment={comment}
      feedItemId={feedItemId}
      handleLike={(): Promise<FetchResult<LikeCommentMutation>> =>
        likeComment({ variables: { commentId: comment.id } })
      }
      isAllocator={isAllocator}
      mentionables={mentionables}
      postId={postId}
      profileImageUrl={profileImageUrl}
      showLikes={showLikes}
    />
  );
};

export default CommentContainer;
